/**
 * Preview Journey State — one continuous state machine.
 *
 * The Preview Journey and the future Subscriber Journey are the same
 * behavioural sequence. Only the data source differs (Preview = sample,
 * Subscriber = user's own).
 *
 * Preview state lives in localStorage. It survives:
 *  - navigation between routes (React and frozen iframe pages),
 *  - browser refresh,
 *  - closing and re-opening the tab.
 *
 * Schema (single active journey):
 *   {
 *     book:  "atomic-habits",              // showcase slug
 *     picks: ["0","2","4"],                // Sankalpa data-i indices
 *     stage: "bodha" | "sankalpa" | "craftprint" | "sadhana" | "workspace",
 *     updatedAt: <iso timestamp>
 *   }
 *
 * The same schema is read/written by:
 *  - This React module (BookDetailPage, MyCraftzPage)
 *  - /_mockups/v2/embed.js on the frozen pages
 *
 * Do NOT rename these keys without also updating embed.js.
 */

const KEY = "icraftz.preview.journey.v1";

const VALID_STAGES = ["bodha", "sankalpa", "craftprint", "sadhana", "workspace"];

function safeParse() {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== "object") return null;
    if (!obj.book) return null;
    return obj;
  } catch {
    return null;
  }
}

export function getJourney() {
  return safeParse();
}

/** Overwrite the entire journey (used when starting a new one). */
export function startJourney(book) {
  const next = {
    book: String(book || "").toLowerCase(),
    picks: [],
    stage: "bodha",
    updatedAt: new Date().toISOString(),
  };
  window.localStorage.setItem(KEY, JSON.stringify(next));
  return next;
}

/** Merge fields into the existing journey. Creates one if missing. */
export function patchJourney(patch) {
  const cur = safeParse() || {
    book: patch.book || "",
    picks: [],
    stage: "bodha",
  };
  const next = {
    ...cur,
    ...patch,
    updatedAt: new Date().toISOString(),
  };
  if (next.stage && !VALID_STAGES.includes(next.stage)) delete next.stage;
  window.localStorage.setItem(KEY, JSON.stringify(next));
  return next;
}

export function clearJourney() {
  window.localStorage.removeItem(KEY);
}

/**
 * Build the URL for a given stage, always preserving preview mode + book.
 * Used by every stage transition in the React app.
 */
export function stageUrl(stage, opts = {}) {
  const j = safeParse();
  const book = opts.book || j?.book || "";
  const params = new URLSearchParams();
  params.set("mode", "preview");
  if (book) params.set("book", book);
  if (opts.extra) {
    Object.entries(opts.extra).forEach(([k, v]) => params.set(k, v));
  }
  const map = {
    bodha:      "/bodha",
    sankalpa:   "/sankalpa",
    craftprint: "/mycraftz",  // /mycraftz?install=<book>
    sadhana:    "/sadhana",
    workspace:  "/mycraftz",
  };
  if (stage === "craftprint" && book) {
    params.set("install", book);
  }
  return `${map[stage] || "/"}?${params.toString()}`;
}

/**
 * True if the current page is running in Preview mode. A page is in
 * Preview mode when either:
 *  - the URL carries ?mode=preview, OR
 *  - localStorage holds an active journey (survives back-nav / refresh).
 */
export function isPreviewMode(search) {
  const params = new URLSearchParams(search || (typeof window !== "undefined" ? window.location.search : ""));
  if (params.get("mode") === "preview") return true;
  return !!safeParse();
}
